type ErrorPattern = {
  test: (error: string) => boolean;
  message: string;
};

const ERROR_PATTERNS: ErrorPattern[] = [
  {
    test: (e) => /rate.?limit|429|too many request/i.test(e),
    message: "Too many requests — please try again in a few minutes",
  },
  {
    test: (e) => /timeout|timed?.?out|ETIMEDOUT/i.test(e),
    message: "The request timed out — please try again",
  },
  {
    test: (e) => /ECONNRESET|ECONNREFUSED|connection.*(reset|refused|closed)/i.test(e),
    message: "Connection was interrupted — please try again",
  },
  {
    test: (e) => /503|service.?unavailable/i.test(e),
    message: "The service is temporarily unavailable",
  },
  {
    test: (e) => /S3|retrieve.*token|storage.*key/i.test(e),
    message: "The service is temporarily unavailable",
  },
  {
    test: (e) => /unauthorized|401|forbidden|403|access.?denied/i.test(e),
    message: "Authorization failed — please reconnect your TikTok account",
  },
  {
    test: (e) => /not.?found|404/i.test(e),
    message: "This item could not be found — it may have been removed",
  },
  {
    test: (e) => /invalid.*response|unexpected.*token|JSON/i.test(e),
    message: "Received an unexpected response — please try again",
  },
  {
    test: (e) => /webhook|lark|card/i.test(e),
    message: "Could not create the ticket — please try again",
  },
  {
    // Matches the friendly string the worker emits when TikTok rejects a creator for being on
    // another target collab for the same product, and TikTok's short opaque code "already_in".
    // Kept narrow so unrelated errors containing the word "conflict" (e.g. TikTok's edit-lock
    // concurrency error, handled below) don't get mis-translated to this message.
    test: (e) => /already[ _]in[ _]another collaboration|already_in\b/i.test(e),
    message: "Creator is already in another collaboration for the same product",
  },
  {
    // TikTok returns this when another mutation has the collaboration's invitation list locked
    // (e.g. concurrent bulk invites racing). Distinct from the creator-already-in-collab case.
    test: (e) => /(invitation[ _])?edit[ _-]?lock/i.test(e),
    message: "Another change to this collaboration is still in progress — please wait a moment and try again",
  },
  {
    // Matches the friendly string the worker emits when TikTok rejects a creator's open id via
    // `invalid_open_id_list` on the publish CREATE — typically a creator account that's gone,
    // suspended, or whose region doesn't match the shop.
    test: (e) => /open[ _-]?id (was )?(invalid|rejected)|invalid open[ _-]?id|tiktok rejected.*invalid/i.test(e),
    message:
      "TikTok couldn't accept this creator — their account may be unavailable in your region or no longer exists",
  },
  {
    // publishDraft / applyDraftInvitationSideEffects emit this when a username can't be matched
    // to a TikTok creator_open_id from any source (local creators table, global candidates table,
    // or live TikTok lookup) — usually a stale handle or a creator who's no longer on TikTok.
    test: (e) => /could not (?:resolve|find) creator on tiktok|could not resolve any creator usernames/i.test(e),
    message: "We couldn't find this creator on TikTok — their handle may have changed or the account may be gone",
  },
];

export const translateError = (technicalError: string): string => {
  for (const pattern of ERROR_PATTERNS) {
    if (pattern.test(technicalError)) return pattern.message;
  }
  return "Something went wrong — please try again or contact support";
};

export const ACTION_LABELS: Record<string, { inProgress: string; success: string; partialFailure: string }> = {
  contact_creator: {
    inProgress: "Sending messages to creators",
    success: "All messages sent successfully",
    partialFailure: "Some messages couldn't be sent",
  },
  remove_from_collaboration: {
    inProgress: "Removing creators from collaborations",
    success: "All creators removed successfully",
    partialFailure: "Some creators couldn't be removed",
  },
  remove_from_ad_campaign: {
    inProgress: "Removing videos from ad campaigns",
    success: "All videos removed successfully",
    partialFailure: "Some videos couldn't be removed",
  },
  demonetization_ticket: {
    inProgress: "Creating demonetization tickets",
    success: "All tickets created successfully",
    partialFailure: "Some tickets couldn't be created",
  },
  takedown_request: {
    inProgress: "Creating takedown requests",
    success: "All requests created successfully",
    partialFailure: "Some requests couldn't be created",
  },
  message_creator: {
    inProgress: "Sending messages to creators",
    success: "All messages sent successfully",
    partialFailure: "Some messages couldn't be sent",
  },
  invite_to_collaboration: {
    inProgress: "Inviting creators to collaboration",
    success: "All creators invited successfully",
    partialFailure: "Some creators couldn't be invited",
  },
  import_blocklist: {
    inProgress: "Blocking creators",
    success: "All creators blocked successfully",
    partialFailure: "Some creators couldn't be blocked",
  },
  apply_sample_rule: {
    inProgress: "Applying rule",
    success: "Rule applied",
    partialFailure: "Some applications couldn't be decided",
  },
};
